// Copyright 2025, University of Maryland, All Rights Reserved
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { configApi } from '../api/config';
import type { ConfigurationLevel } from '../types/config';

interface UseConfigMutationsOptions {
  level: ConfigurationLevel;
  orgId?: string;
  wsId?: string;
  onSuccess?: () => void;
}

/**
 * Custom hook that provides all configuration mutations (create, update, delete)
 * Automatically handles query invalidation and success callbacks
 */
export function useConfigMutations({
  level,
  orgId,
  wsId,
  onSuccess,
}: UseConfigMutationsOptions) {
  const queryClient = useQueryClient();

  // Template Mutations
  const createTemplate = useMutation({
    mutationFn: ({ name, content }: { name: string; content: Record<string, any> }) =>
      configApi.createTemplate(level, name, content, orgId, wsId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: level === 'default'
          ? ['templates', level]
          : ['templates', level, orgId, wsId]
      });
      onSuccess?.();
    },
  });

  const updateTemplate = useMutation({
    mutationFn: ({ name, content }: { name: string; content: Record<string, any> }) =>
      configApi.updateTemplate(level, name, content, orgId, wsId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: level === 'default'
          ? ['templates', level]
          : ['templates', level, orgId, wsId]
      });
      onSuccess?.();
    },
  });

  const deleteTemplate = useMutation({
    mutationFn: (name: string) => configApi.deleteTemplate(level, name, orgId, wsId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: level === 'default'
          ? ['templates', level]
          : ['templates', level, orgId, wsId]
      });
      onSuccess?.();
    },
  });

  const getTemplate = useMutation({
    mutationFn: ({ name }: { name: string }) =>
      configApi.getTemplate(level, name, orgId, wsId),
  });

  // Prompt Mutations
  const createPrompt = useMutation({
    mutationFn: ({ name, content, analysisType: mutationAnalysisType }: { name: string; content: string; analysisType?: string }) =>
      configApi.createPrompt(level, name, content, orgId, wsId, mutationAnalysisType),
    onSuccess: () => {
      // Invalidate all prompt queries that match this level and org/ws
      // This will catch both filtered and unfiltered queries
      queryClient.invalidateQueries({ 
        queryKey: ['prompts'],
        refetchType: 'active'
      });
      onSuccess?.();
    },
  });

  const updatePrompt = useMutation({
    mutationFn: ({ name, content, analysisType }: { name: string; content: string; analysisType?: string }) =>
      configApi.updatePrompt(level, name, content, orgId, wsId, analysisType),
    onSuccess: () => {
      // Invalidate all prompt queries
      queryClient.invalidateQueries({ 
        queryKey: ['prompts'],
        refetchType: 'active'
      });
      onSuccess?.();
    },
  });

  const deletePrompt = useMutation({
    mutationFn: ({ name, analysisType }: { name: string; analysisType?: string }) =>
      configApi.deletePrompt(level, name, orgId, wsId, analysisType),
    onSuccess: () => {
      // Invalidate all prompt queries
      queryClient.invalidateQueries({ 
        queryKey: ['prompts'],
        refetchType: 'active'
      });
      onSuccess?.();
    },
  });

  const getPrompt = useMutation({
    mutationFn: ({ name, analysisType }: { name: string; analysisType?: string }) =>
      configApi.getPrompt(level, name, orgId, wsId, analysisType),
  });

  // Migration Mutation (admin only)
  const migrateConfigs = useMutation({
    mutationFn: () => configApi.migrateConfigs(),
  });

  return {
    // Template mutations
    createTemplate,
    updateTemplate,
    deleteTemplate,
    getTemplate,
    // Prompt mutations
    createPrompt,
    updatePrompt,
    deletePrompt,
    getPrompt,
    // Admin mutations
    migrateConfigs,
  };
}

export type UseConfigMutationsResult = ReturnType<typeof useConfigMutations>;
